import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Divider, Drawer, Form, Input, Modal, Popconfirm, Space, Table, Tag, message, Select, Tabs, Tooltip, Switch, Typography, Dropdown } from "antd";
import { FaArchive, FaChartLine, FaCheck, FaEye, FaFileAlt, FaFire, FaPen, FaTrash, FaUndo, FaGripVertical, FaEllipsisV } from "react-icons/fa";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";

const buildSkillTree = (flatList) => {
  const map = {};
  const roots = [];

  // Clone nodes and prepare children arrays
  flatList.forEach((node) => {
    map[node._id] = { ...node, children: [] };
  });

  // Attach children to parents or place at root
  flatList.forEach((node) => {
    if (node.parent && node.parent._id && map[node.parent._id]) {
      map[node.parent._id].children.push(map[node._id]);
    } else {
      roots.push(map[node._id]);
    }
  });

  // Remove empty children arrays to prevent false expand icons in Table
  Object.values(map).forEach((node) => {
    if (node.children.length === 0) delete node.children;
  });

  return roots;
};

const PAGE_STATUS_COLORS = {
  missing: "default",
  draft: "gold",
  published: "green",
  archived: "red",
};

const PAGE_STATUS_LABELS = {
  missing: "Missing",
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

const PAGE_STATUS_DESCRIPTIONS = {
  missing: "No editable page document exists yet.",
  draft: "Editable internally; not served by public API.",
  published: "Live on public skill pages.",
  archived: "Kept for recovery; hidden from public API.",
};

const STAGE_LEVEL_LABELS = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

const emptyStringArray = (value) => (Array.isArray(value) ? value : []);

const normalizePageFormValues = (values) => ({
  ...values,
  seo: {
    ...(values.seo || {}),
    keywords: emptyStringArray(values.seo?.keywords).filter(Boolean),
  },
  quickStats: values.quickStats || {},
  overview: {
    ...(values.overview || {}),
    whereUsed: emptyStringArray(values.overview?.whereUsed).filter(Boolean),
  },
  whoShouldLearn: emptyStringArray(values.whoShouldLearn).filter(Boolean),
  relatedEcosystems: emptyStringArray(values.relatedEcosystems).filter(Boolean),
  careerOutcomes: emptyStringArray(values.careerOutcomes).filter((item) => item?.role || item?.description),
  faq: emptyStringArray(values.faq).filter((item) => item?.question || item?.answer),
  skillPairings: emptyStringArray(values.skillPairings).filter((item) => item?.slug || item?.name),
  stages: emptyStringArray(values.stages).map(({ level, id, ...stage }) => stage),
});

const getApiErrorMessage = (error, fallback) => (
  error.response?.data?.error?.message ||
  error.response?.data?.message ||
  error.message ||
  fallback
);

const StringList = ({ name, addLabel, placeholder }) => (
  <Form.List name={name}>
    {(fields, { add, remove }) => (
      <div>
        {fields.map((field) => (
          <Space key={field.key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
            <Form.Item {...field} style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder={placeholder} />
            </Form.Item>
            <Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>
          </Space>
        ))}
        <Button type="dashed" onClick={() => add()} block>{addLabel}</Button>
      </div>
    )}
  </Form.List>
);

const IconAction = ({ title, color, icon, onClick, danger = false, disabled = false }) => (
  <Tooltip title={title}>
    <Button
      type="text"
      size="small"
      shape="circle"
      danger={danger}
      disabled={disabled}
      icon={icon}
      onClick={onClick}
      style={{
        color: disabled || danger ? undefined : color,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  </Tooltip>
);

const StatusPill = ({ status }) => (
  <Tag color={PAGE_STATUS_COLORS[status] || "default"} style={{ marginRight: 0, fontWeight: 700 }}>
    {PAGE_STATUS_LABELS[status] || status}
  </Tag>
);

const StatCard = ({ label, value, tone }) => (
  <Card size="small" style={{ borderRadius: 8, borderColor: "#edf1f7", height: "100%" }}>
    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: tone || "#0f172a", lineHeight: 1.2 }}>{value}</div>
  </Card>
);

const SkillList = () => {
  // --- Skills State ---
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [dragOverSkillId, setDragOverSkillId] = useState(null);

  // --- Topics State ---
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [savingTopic, setSavingTopic] = useState(false);
  const [topicForm] = Form.useForm();
  const [pageForm] = Form.useForm();
  const [topicSearchText, setTopicSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("skills");
  const [isPageDrawerOpen, setIsPageDrawerOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [savingPage, setSavingPage] = useState(false);
  const [previewPage, setPreviewPage] = useState(null);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const response = await axiosHttp.get("/v1/skill");
      if (response.data?.status) {
        setSkills(response.data.results || []);
      } else {
        message.error(response.data?.message || "Failed to fetch skills");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to fetch skills");
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async () => {
    setLoadingTopics(true);
    try {
      const response = await axiosHttp.get("/v3/admin/topics");
      if (response.data?.data || response.data?.results || Array.isArray(response.data)) {
        setTopics(response.data.data || response.data.results || response.data || []);
      } else {
        message.error("Failed to fetch topics");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to fetch topics");
    } finally {
      setLoadingTopics(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosHttp.get("/v1/category");
      if (response.data?.status) {
        setCategories(response.data.results || []);
      }
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  useEffect(() => {
    fetchSkills();
    fetchCategories();
    fetchTopics();
  }, []);

  // --- Skill Methods ---
  const openCreateModal = () => {
    setEditingSkill(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    form.setFieldsValue({
      name: skill.name,
      code: skill.code,
      slug: skill.slug,
      parent: skill.parent ? skill.parent._id : null,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = { ...values, parent: values.parent || null };

      if (editingSkill?._id) {
        const response = await axiosHttp.put("/v1/skill", {
          id: editingSkill._id,
          ...payload,
        });
        if (!response.data?.status) throw new Error(response.data?.message || "Update failed");
        message.success("Skill updated successfully.");
      } else {
        const response = await axiosHttp.post("/v1/skill", payload);
        if (!response.data?.status) throw new Error(response.data?.message || "Create failed");
        message.success("Skill created successfully.");
      }

      setIsModalOpen(false);
      fetchSkills();
      if (activeTab === "topics") fetchTopics();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || error.response?.data?.message || "Failed to save skill");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (skill) => {
    try {
      const response = await axiosHttp.delete("/v1/skill", {
        data: { id: skill._id },
      });
      if (!response.data?.status) throw new Error(response.data?.message || "Delete failed");
      message.success("Skill deleted successfully.");
      fetchSkills();
      if (activeTab === "topics") fetchTopics();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to delete skill");
    }
  };

  const handleDragDropParentChange = async (draggedId, targetParentId) => {
    const draggedSkill = skills.find(s => s._id === draggedId);
    const targetParent = skills.find(s => s._id === targetParentId);
    if (!draggedSkill || !targetParent) return;

    const isAncestor = (ancestorId, childId) => {
      let current = skills.find(s => s._id === childId);
      while (current && current.parent) {
        const pId = current.parent._id || current.parent;
        if (pId === ancestorId) return true;
        current = skills.find(s => s._id === pId);
      }
      return false;
    };

    if (draggedId === targetParentId) {
      message.error("A skill cannot be its own parent.");
      return;
    }

    if (isAncestor(draggedId, targetParentId)) {
      message.error("Cyclic relationship detected. Cannot place a parent skill under its own child.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: draggedSkill.name,
        code: draggedSkill.code,
        slug: draggedSkill.slug,
        parent: targetParentId,
      };

      const response = await axiosHttp.put("/v1/skill", {
        id: draggedId,
        ...payload,
      });

      if (!response.data?.status) throw new Error(response.data?.message || "Reparenting failed");
      
      // Update local state directly to guarantee immediate refresh
      setSkills(prevSkills => {
        return prevSkills.map(s => {
          if (s._id === draggedId) {
            return {
              ...s,
              parent: {
                _id: targetParentId,
                name: targetParent.name
              }
            };
          }
          return s;
        });
      });

      message.success(`Reparented "${draggedSkill.name}" under "${targetParent.name}".`);
      fetchSkills();
    } catch (error) {
      message.error(error.message || "Failed to update skill parent relationship.");
    } finally {
      setLoading(false);
    }
  };

  // --- Topic Methods ---
  const openCreateTopicModal = () => {
    setEditingTopic(null);
    topicForm.resetFields();
    topicForm.setFieldsValue({ is_trending: false, is_popular: false, description: '' });
    setIsTopicModalOpen(true);
  };

  const openCreateTopicModalWithSkill = (skillId) => {
    setEditingTopic(null);
    topicForm.resetFields();
    topicForm.setFieldsValue({ 
      skill_id: skillId,
      is_trending: false,
      is_popular: false,
      description: ''
    });
    setIsTopicModalOpen(true);
  };

  const openEditTopicModal = (topic) => {
    setEditingTopic(topic);
    const existingTags = topic.tags || [];
    topicForm.setFieldsValue({
      skill_id: topic._id,
      categories: topic.categories ? topic.categories.map(c => c._id || c) : [],
      is_trending: existingTags.includes('trending'),
      is_popular: existingTags.includes('popular'),
      description: topic.description || ''
    });
    setIsTopicModalOpen(true);
  };

  // Helper: build the tags array by merging trait keywords with any existing non-trait tags
  const buildTraitTags = (existingTags = [], isTrending, isPopular) => {
    const TRAIT_KEYWORDS = ['trending', 'popular'];
    // Keep all non-trait tags (custom ones users may have added)
    const nonTraitTags = existingTags.filter(t => !TRAIT_KEYWORDS.includes(t));
    const traitTags = [
      ...(isTrending ? ['trending'] : []),
      ...(isPopular ? ['popular'] : []),
    ];
    return [...nonTraitTags, ...traitTags];
  };

  const handleTopicSubmit = async () => {
    try {
      const values = await topicForm.validateFields();
      setSavingTopic(true);

      // Get the skill's existing tags to preserve non-trait ones
      const existingSkill = editingTopic || {};
      const existingTags = existingSkill.tags || [];
      const newTags = buildTraitTags(existingTags, values.is_trending, values.is_popular);

      const payload = {
        tags: newTags,
        categories: values.categories || [],
        description: values.description || ''
      };

      const response = await axiosHttp.put(`/v3/admin/topics/${values.skill_id}`, payload);
      if (!response.data?.data) throw new Error(response.data?.message || "Operation failed");
      
      message.success("Topic saved successfully.");
      setIsTopicModalOpen(false);
      fetchTopics();
      if (activeTab === "skills") fetchSkills();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || error.response?.data?.message || "Failed to save topic");
    } finally {
      setSavingTopic(false);
    }
  };

  const handleRemoveTopic = async (topic) => {
    try {
      const response = await axiosHttp.delete(`/v3/admin/topics/${topic._id}`);
      if (!response.data?.data) throw new Error(response.data?.message || "Failed to remove topic");
      
      message.success("Topic removed successfully.");
      fetchTopics();
      if (activeTab === "skills") fetchSkills();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to remove topic");
    }
  };

  const toggleTrending = async (topic) => {
    try {
      const currentTags = topic.tags || [];
      const isTrending = currentTags.includes('trending');
      const newTags = buildTraitTags(currentTags, !isTrending, currentTags.includes('popular'));
      const payload = { tags: newTags, categories: (topic.categories || []).map((c) => c._id || c) };
      const response = await axiosHttp.put(`/v3/admin/topics/${topic._id}`, payload);
      if (!response.data?.data) throw new Error(response.data?.message || "Failed to update trending status");
      
      message.success(`Topic ${!isTrending ? "marked as trending" : "removed from trending"}.`);
      fetchTopics();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to update trending status");
    }
  };

  const togglePopular = async (topic) => {
    try {
      const currentTags = topic.tags || [];
      const isPopular = currentTags.includes('popular');
      const newTags = buildTraitTags(currentTags, currentTags.includes('trending'), !isPopular);
      const payload = { tags: newTags, categories: (topic.categories || []).map((c) => c._id || c) };
      const response = await axiosHttp.put(`/v3/admin/topics/${topic._id}`, payload);
      if (!response.data?.data) throw new Error(response.data?.message || "Failed to update popularity status");
      
      message.success(`Topic ${!isPopular ? "marked as popular" : "removed from popular"}.`);
      fetchTopics();
    } catch (error) {
      message.error(error.message || error.response?.data?.message || "Failed to update popularity status");
    }
  };

  const createOrRepairPage = async (topic) => {
    try {
      const response = await axiosHttp.post(`/v3/admin/topics/${topic._id}/page`);
      if (!response.data?.data) throw new Error("Failed to create skill page");
      message.success("Draft skill page is ready.");
      fetchTopics();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to create skill page"));
    }
  };

  const openPageEditor = async (topic) => {
    if (!topic.page?._id) {
      await createOrRepairPage(topic);
      return;
    }
    setLoadingPage(true);
    setIsPageDrawerOpen(true);
    try {
      const response = await axiosHttp.get(`/v3/admin/skill-pages/${topic.page._id}`);
      const page = response.data?.data;
      if (!page) throw new Error("Skill page not found");
      setEditingPage(page);
      pageForm.setFieldsValue({
        ...page,
        stages: page.stages || [],
        seo: {
          keywords: [],
          ...(page.seo || {}),
        },
        quickStats: page.quickStats || {},
        overview: {
          whereUsed: [],
          ...(page.overview || {}),
        },
      });
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load skill page"));
      setIsPageDrawerOpen(false);
    } finally {
      setLoadingPage(false);
    }
  };

  const handlePageSubmit = async () => {
    try {
      const values = await pageForm.validateFields();
      setSavingPage(true);
      const payload = normalizePageFormValues(values);
      const response = await axiosHttp.put(`/v3/admin/skill-pages/${editingPage._id}`, payload);
      const updated = response.data?.data;
      if (!updated) throw new Error("Failed to save skill page");
      setEditingPage(updated);
      pageForm.setFieldsValue(updated);
      message.success("Skill page saved.");
      fetchTopics();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, "Failed to save skill page"));
    } finally {
      setSavingPage(false);
    }
  };

  const changePageStatus = async (topic, status) => {
    if (!topic.page?._id) return;
    try {
      const response = await axiosHttp.patch(`/v3/admin/skill-pages/${topic.page._id}/status`, { status });
      if (!response.data?.data) throw new Error("Failed to update status");
      message.success(`Skill page moved to ${status}.`);
      fetchTopics();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to update page status"));
    }
  };

  const openPreview = async (topic) => {
    if (!topic.page?._id) {
      message.warning("Create a skill page before previewing.");
      return;
    }
    try {
      const response = await axiosHttp.get(`/v3/admin/skill-pages/${topic.page._id}`);
      if (!response.data?.data) throw new Error("Skill page not found");
      setPreviewPage(response.data.data);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load preview"));
    }
  };

  // --- Computations ---
  const skillTableData = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (query) {
      return skills.filter(
        (skill) =>
          String(skill.name || "").toLowerCase().includes(query) ||
          String(skill.code || "").toLowerCase().includes(query) ||
          String(skill.slug || "").toLowerCase().includes(query)
      );
    } 
    return buildSkillTree(skills);
  }, [skills, searchText]);

  const topicTableData = useMemo(() => {
    const query = topicSearchText.trim().toLowerCase();
    if (query) {
      return topics.filter(
        (topic) =>
          String(topic.name || "").toLowerCase().includes(query) ||
          String(topic.code || "").toLowerCase().includes(query)
      );
    }
    return topics;
  }, [topics, topicSearchText]);

  const parentOptions = useMemo(() => {
    return skills
      .filter((s) => !editingSkill || s._id !== editingSkill._id)
      .map((s) => ({ value: s._id, label: s.name }));
  }, [skills, editingSkill]);

  const allSkillsOptions = useMemo(() => {
    return skills.map((s) => ({ value: s._id, label: s.name }));
  }, [skills]);

  const categoryOptions = useMemo(() => {
    return categories.map((c) => ({ value: c._id, label: c.name || c.title || c.code }));
  }, [categories]);

  const topicStats = useMemo(() => {
    const counts = topics.reduce(
      (acc, topic) => {
        const status = topic.page?.status || "missing";
        acc.total += 1;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      { total: 0, missing: 0, draft: 0, published: 0, archived: 0 }
    );
    return counts;
  }, [topics]);

  // --- Columns ---
  const skillColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value, record) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              className="drag-handle"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("draggedSkillId", record._id);
                e.dataTransfer.effectAllowed = "move";
              }}
              style={{
                cursor: "grab",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "4px",
                color: "#94a3b8",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
                e.currentTarget.style.color = "#1890ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#94a3b8";
              }}
            >
              <FaGripVertical style={{ fontSize: "14px" }} />
            </span>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: '15px' }}>{value}</strong>
              {searchText && record.parent && record.parent.name && (
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
                  ↳ Under: {record.parent.name}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (value) => <Tag color="geekblue">{value}</Tag>,
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      render: (value) => (value ? <Tag>{value}</Tag> : "--"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <Tooltip title="Edit Skill">
            <span
              onClick={() => openEditModal(record)}
              style={{
                width: '32px',
                height: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                cursor: 'pointer',
                color: '#1890ff',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FaPen style={{ fontSize: '13px' }} />
            </span>
          </Tooltip>
          <Popconfirm
            title="Delete this skill?"
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Delete Skill">
              <span
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: '#ff4d4f',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff1f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FaTrash style={{ fontSize: '13px' }} />
              </span>
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const topicColumns = [
    {
      title: "Topic",
      dataIndex: "name",
      key: "name",
      width: 260,
      render: (value, record) => (
        <div>
          <strong style={{ fontSize: 15, color: "#0f172a" }}>{value}</strong>
          {record.description ? (
            <Tooltip title={record.description}>
              <div style={{ 
                marginTop: 2, 
                fontSize: 12, 
                color: "#64748b",
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                maxWidth: '240px' 
              }}>
                {record.description}
              </div>
            </Tooltip>
          ) : (
            <div style={{ marginTop: 2, fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              No description
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Categories",
      dataIndex: "categories",
      key: "categories",
      render: (cats) => (
        <>
          {cats && cats.length > 0 ? (
            cats.map((c) => (
              <Tag key={c._id || c} color="cyan" style={{ marginBottom: '4px' }}>
                {c.name || c.title || c.code || 'Category'}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#94a3b8', fontSize: 12 }}>No categories</span>
          )}
        </>
      ),
    },
    {
      title: "Traits",
      key: "traits",
      align: "center",
      width: 140,
      render: (_, record) => {
        const tags = record.tags || [];
        const isTrending = tags.includes('trending');
        const isPopular = tags.includes('popular');
        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {/* Popular Badge (Fire) */}
            <Tooltip title={isPopular ? "Remove from Popular" : "Mark as Popular"}>
              <button
                type="button"
                style={{ 
                  width: '36px',
                  height: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.215, 0.610, 0.355, 1)',
                  backgroundColor: isPopular ? '#fff2e8' : '#f5f5f5',
                  border: isPopular ? '1px solid #ffd591' : '1px solid #e8e8e8',
                  color: isPopular ? '#fa8c16' : '#bfbfbf',
                  boxShadow: isPopular ? '0 2px 8px rgba(250, 140, 22, 0.15)' : 'none',
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                  if (!isPopular) {
                    e.currentTarget.style.backgroundColor = '#fffbe6';
                    e.currentTarget.style.borderColor = '#ffe58f';
                    e.currentTarget.style.color = '#faad14';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  if (!isPopular) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.color = '#bfbfbf';
                  }
                }}
                onClick={() => togglePopular(record)}
              >
                <FaFire style={{ fontSize: '18px' }} />
              </button>
            </Tooltip>

            {/* Trending Badge (ChartLine) */}
            <Tooltip title={isTrending ? "Remove from Trending" : "Mark as Trending"}>
              <button
                type="button"
                style={{ 
                  width: '36px',
                  height: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.215, 0.610, 0.355, 1)',
                  backgroundColor: isTrending ? '#e6f7ff' : '#f5f5f5',
                  border: isTrending ? '1px solid #91d5ff' : '1px solid #e8e8e8',
                  color: isTrending ? '#1890ff' : '#bfbfbf',
                  boxShadow: isTrending ? '0 2px 8px rgba(24, 144, 255, 0.15)' : 'none',
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                  if (!isTrending) {
                    e.currentTarget.style.backgroundColor = '#e6f7ff';
                    e.currentTarget.style.borderColor = '#91d5ff';
                    e.currentTarget.style.color = '#1890ff';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  if (!isTrending) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.color = '#bfbfbf';
                  }
                }}
                onClick={() => toggleTrending(record)}
              >
                <FaChartLine style={{ fontSize: '18px' }} />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Page",
      key: "page",
      width: 120,
      render: (_, record) => {
        const status = record.page?.status || "missing";
        return (
          <Tooltip title={PAGE_STATUS_DESCRIPTIONS[status]} placement="topLeft">
            <div style={{ cursor: "help", display: "inline-block" }}>
              <StatusPill status={status} />
              {record.page?.slug && (
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {record.page.slug}
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 80,
      render: (_, record) => {
        const items = [
          {
            key: "editTopic",
            label: "Edit Topic Settings",
            icon: <FaPen style={{ fontSize: 11 }} />,
            onClick: () => openEditTopicModal(record),
          },
          ...(record.page
            ? [
                {
                  key: "editPage",
                  label: "Edit Page Content",
                  icon: <FaFileAlt style={{ fontSize: 11 }} />,
                  onClick: () => openPageEditor(record),
                },
                {
                  key: "previewPage",
                  label: "Preview Page",
                  icon: <FaEye style={{ fontSize: 11 }} />,
                  onClick: () => openPreview(record),
                },
                record.page.status === "draft" && {
                  key: "publishPage",
                  label: "Publish Page",
                  icon: <FaCheck style={{ fontSize: 11 }} />,
                  onClick: () => changePageStatus(record, "published"),
                },
                record.page.status === "published" && {
                  key: "unpublishPage",
                  label: "Unpublish to Draft",
                  icon: <FaUndo style={{ fontSize: 11 }} />,
                  onClick: () => changePageStatus(record, "draft"),
                },
                record.page.status === "archived"
                  ? {
                      key: "restorePage",
                      label: "Restore to Draft",
                      icon: <FaUndo style={{ fontSize: 11 }} />,
                      onClick: () => changePageStatus(record, "draft"),
                    }
                  : {
                      key: "archivePage",
                      label: "Archive Page",
                      icon: <FaArchive style={{ fontSize: 11 }} />,
                      onClick: () => changePageStatus(record, "archived"),
                    },
              ].filter(Boolean)
            : [
                {
                  key: "createPage",
                  label: "Create Draft Page",
                  icon: <FaFileAlt style={{ fontSize: 11 }} />,
                  onClick: () => createOrRepairPage(record),
                },
              ]),
          {
            type: "divider",
          },
          {
            key: "removeTopic",
            label: "Remove Topic",
            danger: true,
            icon: <FaTrash style={{ fontSize: 11 }} />,
            onClick: () => {
              Modal.confirm({
                title: "Remove from Topics?",
                content: "This will not delete the skill, only unmark it as a topic.",
                okText: "Remove",
                cancelText: "Cancel",
                okButtonProps: { danger: true },
                onOk: () => handleRemoveTopic(record),
              });
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button
              type="text"
              shape="circle"
              icon={<FaEllipsisV style={{ fontSize: 13, color: "#64748b" }} />}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          </Dropdown>
        );
      },
    },
  ];

  const tabItems = [
    {
      key: "skills",
      label: "Skills",
      children: (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div style={{ width: '300px' }}>
                <Input
                  placeholder="Search by name, code, or slug"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ borderRadius: '6px', borderColor: '#d9d9d9', height: '38px', margin: 0 }}
                />
              </div>
              <Button 
                type="primary" 
                onClick={openCreateModal}
                style={{ height: '38px', borderRadius: '6px' }}
              >
                Add Skill
              </Button>
            </div>
             <Table
                rowKey={(record) => record._id}
                columns={skillColumns}
                dataSource={skillTableData}
                loading={loading}
                pagination={{ pageSize: 50 }}
                onRow={(record) => ({
                  onDragOver: (e) => {
                    e.preventDefault();
                    if (dragOverSkillId !== record._id) {
                      setDragOverSkillId(record._id);
                    }
                  },
                  onDragLeave: () => {
                    if (dragOverSkillId === record._id) {
                      setDragOverSkillId(null);
                    }
                  },
                  onDrop: async (e) => {
                    e.preventDefault();
                    setDragOverSkillId(null);
                    const draggedSkillId = e.dataTransfer.getData("draggedSkillId");
                    if (draggedSkillId && draggedSkillId !== record._id) {
                      handleDragDropParentChange(draggedSkillId, record._id);
                    }
                  },
                  style: {
                    backgroundColor: dragOverSkillId === record._id ? "#e6f7ff" : undefined,
                    border: dragOverSkillId === record._id ? "1px dashed #1890ff" : undefined,
                  }
                })}
              />
          </div>
        </div>
      )
    },
    {
      key: "topics",
      label: "Topics",
      children: (
        <div>
          <div className="row mb-3">
            <div className="col-md-3 mb-2"><StatCard label="Topics" value={topicStats.total} /></div>
            <div className="col-md-3 mb-2"><StatCard label="Published" value={topicStats.published} tone="#16a34a" /></div>
            <div className="col-md-3 mb-2"><StatCard label="Drafts" value={topicStats.draft} tone="#ca8a04" /></div>
            <div className="col-md-3 mb-2"><StatCard label="Missing Pages" value={topicStats.missing} tone="#64748b" /></div>
          </div>

          <Card
            style={{ borderRadius: 8, borderColor: "#edf1f7" }}
            bodyStyle={{ padding: 18 }}
          >
            <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: 16, flexWrap: "wrap" }}>
              <div>
                <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>User-facing Topics</h4>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                  Promote skills into public topic pages, edit editorial content, and control publication state.
                </div>
              </div>
              <Space wrap>
                <div style={{ width: 360 }}>
                <Select
                  showSearch
                  placeholder="Search any skill to make it a topic..."
                  style={{ width: '100%' }}
                  optionFilterProp="label"
                  options={allSkillsOptions}
                  value={null}
                  onChange={openCreateTopicModalWithSkill}
                />
                </div>
                <div style={{ width: 220 }}>
                  <Input
                    placeholder="Search topics..."
                    value={topicSearchText}
                    onChange={(e) => setTopicSearchText(e.target.value)}
                    style={{ borderRadius: '6px', borderColor: '#d9d9d9', height: '38px', margin: 0 }}
                  />
                </div>
                <Button 
                  type="primary" 
                  onClick={openCreateTopicModal}
                  style={{ height: '38px', borderRadius: '6px' }}
                >
                  Add Topic
                </Button>
              </Space>
            </div>
            {topicStats.missing > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 14 }}
                message={`${topicStats.missing} topic${topicStats.missing === 1 ? "" : "s"} need a draft page document before editors can publish content.`}
              />
            )}
            <Table
              rowKey={(record) => record._id}
              columns={topicColumns}
              dataSource={topicTableData}
              loading={loadingTopics}
              size="middle"
              scroll={{ x: 1100 }}
              pagination={{ pageSize: 50 }}
            />
          </Card>
        </div>
      )
    }
  ];

  return (
    <Page title="Skills & Topics">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="mb-3">
            <h3 className="mb-0">Skills & Topics Administration</h3>
          </div>

          <Tabs 
            activeKey={activeTab} 
            onChange={(key) => setActiveTab(key)} 
            items={tabItems}
            type="card"
          />

          {/* Skill Modal */}
          <Modal
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            onOk={handleSubmit}
            okText={editingSkill ? "Update" : "Create"}
            confirmLoading={saving}
            title={editingSkill ? "Edit Skill" : "Add Skill"}
          >
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Parent Skill"
                name="parent"
                extra="Leave blank to create a top-level macro skill."
              >
                <Select
                  placeholder="Select a Parent (Optional)"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={parentOptions}
                />
              </Form.Item>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Skill name is required" }]}
              >
                <Input placeholder="e.g. MongoDB Aggregation" />
              </Form.Item>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: "Skill code is required" }]}
              >
                <Input placeholder="e.g. mongodb_aggregation" />
              </Form.Item>
              <Form.Item label="Slug" name="slug">
                <Input placeholder="e.g. mongodb-aggregation" />
              </Form.Item>
            </Form>
          </Modal>

          {/* Topic Modal */}
          <Modal
            open={isTopicModalOpen}
            onCancel={() => setIsTopicModalOpen(false)}
            onOk={handleTopicSubmit}
            okText={editingTopic ? "Update Topic" : "Add Topic"}
            confirmLoading={savingTopic}
            title={editingTopic ? "Edit Topic Settings" : "Add Topic"}
          >
            <Form layout="vertical" form={topicForm}>
              <Form.Item
                label="Skill"
                name="skill_id"
                rules={[{ required: true, message: "Please select a skill" }]}
                extra={editingTopic ? "Skill cannot be changed while editing." : "Search and select a skill to promote to a topic."}
              >
                <Select
                  placeholder="Select a Skill"
                  showSearch
                  optionFilterProp="label"
                  options={allSkillsOptions}
                  disabled={!!editingTopic}
                />
              </Form.Item>
              
              <Form.Item
                label="Categories"
                name="categories"
                extra="Assign this topic to one or more categories."
              >
                <Select
                  mode="multiple"
                  placeholder="Select Categories"
                  showSearch
                  optionFilterProp="label"
                  options={categoryOptions}
                />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
                extra="Short description of this topic for public pages."
              >
                <Input.TextArea rows={3} placeholder="Enter description..." />
              </Form.Item>

              <div className="row">
                <div className="col-md-6">
                  <Form.Item
                    label="Popular"
                    name="is_popular"
                    valuePropName="checked"
                    extra="Mark this topic as popular."
                  >
                    <Switch checkedChildren={<FaFire />} unCheckedChildren={<FaFire />} />
                  </Form.Item>
                </div>
                <div className="col-md-6">
                  <Form.Item
                    label="Trending"
                    name="is_trending"
                    valuePropName="checked"
                    extra="Mark this topic as trending."
                  >
                    <Switch checkedChildren={<FaChartLine />} unCheckedChildren={<FaChartLine />} />
                  </Form.Item>
                </div>
              </div>
            </Form>
          </Modal>

          <Drawer
            open={isPageDrawerOpen}
            onClose={() => setIsPageDrawerOpen(false)}
            title={editingPage ? `Edit Skill Page: ${editingPage.name}` : "Edit Skill Page"}
            width={1040}
            bodyStyle={{ background: "#f8fafc", padding: 18 }}
            extra={
              <Space>
                <Button onClick={() => setIsPageDrawerOpen(false)}>Close</Button>
                <Button type="primary" loading={savingPage} onClick={handlePageSubmit}>Save Page</Button>
              </Space>
            }
          >
            {loadingPage ? (
              <Alert message="Loading skill page..." type="info" showIcon />
            ) : (
              <Form layout="vertical" form={pageForm}>
                <Card size="small" style={{ marginBottom: 14, borderRadius: 8, borderColor: "#e2e8f0" }}>
                  <div className="d-flex justify-content-between align-items-start" style={{ gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                        Skill Page Document
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                        {editingPage?.name || "Untitled skill page"}
                      </div>
                      <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                        /skills/{editingPage?.slug || "draft-slug"}
                      </div>
                    </div>
                    <Space wrap>
                      <StatusPill status={editingPage?.status || "draft"} />
                      <Tag color="blue">3 fixed stages</Tag>
                      {editingPage?.updated_at && <Tag>Updated {new Date(editingPage.updated_at).toLocaleDateString()}</Tag>}
                    </Space>
                  </div>
                </Card>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Roadmap stage levels are fixed in v1. Edit labels and copy, but level mapping stays beginner, intermediate, advanced."
                />

                <Divider orientation="left">Identity</Divider>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Item label="Name" name="name" rules={[{ required: true, message: "Name is required" }]}>
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="col-md-3">
                    <Form.Item label="Slug" name="slug" rules={[{ required: true, message: "Slug is required" }]}>
                      <Input disabled={editingPage?.status !== "draft"} />
                    </Form.Item>
                  </div>
                  <div className="col-md-3">
                    <Form.Item label="Status">
                      <Tag color={PAGE_STATUS_COLORS[editingPage?.status]}>{String(editingPage?.status || "").toUpperCase()}</Tag>
                    </Form.Item>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-3"><Form.Item label="Icon" name="icon"><Input /></Form.Item></div>
                  <div className="col-md-3"><Form.Item label="Color" name="color"><Input placeholder="#14b8a6" /></Form.Item></div>
                  <div className="col-md-6"><Form.Item label="Demand Signal" name="demandSignal"><Input /></Form.Item></div>
                </div>

                <Divider orientation="left">SEO</Divider>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Item label="Primary Keyword" name={["seo", "primaryKeyword"]} rules={[{ required: true, message: "Primary keyword is required" }]}>
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="col-md-6">
                    <Form.Item label="Title Suffix" name={["seo", "titleSuffix"]}>
                      <Input />
                    </Form.Item>
                  </div>
                </div>
                <Form.Item label="Short Description" name={["seo", "shortDescription"]} rules={[{ required: true, message: "Short description is required" }]}>
                  <Input.TextArea rows={3} />
                </Form.Item>
                <Form.Item label="Keywords">
                  <StringList name={["seo", "keywords"]} addLabel="Add Keyword" placeholder="Keyword" />
                </Form.Item>

                <Divider orientation="left">Overview</Divider>
                <Form.Item label="Subtitle" name="subtitle"><Input /></Form.Item>
                <Form.Item label="Description" name={["overview", "description"]}><Input.TextArea rows={3} /></Form.Item>
                <Form.Item label="Why Relevant" name={["overview", "whyRelevant"]}><Input.TextArea rows={3} /></Form.Item>
                <Form.Item label="Industry Demand" name={["overview", "industryDemand"]}><Input.TextArea rows={2} /></Form.Item>
                <Form.Item label="Where Used">
                  <StringList name={["overview", "whereUsed"]} addLabel="Add Use Case" placeholder="Use case" />
                </Form.Item>
                <Form.Item label="Curation Text" name="curationText"><Input.TextArea rows={2} /></Form.Item>

                <Divider orientation="left">Quick Stats</Divider>
                <div className="row">
                  <div className="col-md-3"><Form.Item label="Paths" name={["quickStats", "paths"]}><Input type="number" /></Form.Item></div>
                  <div className="col-md-3"><Form.Item label="Courses" name={["quickStats", "courses"]}><Input type="number" /></Form.Item></div>
                  <div className="col-md-3"><Form.Item label="Salary Range" name={["quickStats", "salaryRange"]}><Input /></Form.Item></div>
                  <div className="col-md-3"><Form.Item label="Level" name={["quickStats", "level"]}><Input /></Form.Item></div>
                </div>

                <Divider orientation="left">Audience And Outcomes</Divider>
                <Form.Item label="Who Should Learn">
                  <StringList name="whoShouldLearn" addLabel="Add Audience" placeholder="Audience" />
                </Form.Item>
                <div className="row">
                  <div className="col-md-4"><Form.Item label="Salary Range" name="salaryRange"><Input /></Form.Item></div>
                  <div className="col-md-4"><Form.Item label="Industry Trend" name="industryTrend"><Input /></Form.Item></div>
                  <div className="col-md-4"><Form.Item label="Learning Time Estimate" name="learningTimeEstimate"><Input /></Form.Item></div>
                </div>
                <Form.List name="careerOutcomes">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.map((field) => (
                        <Card key={field.key} size="small" style={{ marginBottom: 12 }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                          <div className="row">
                            <div className="col-md-6"><Form.Item label="Role" name={[field.name, "role"]}><Input /></Form.Item></div>
                            <div className="col-md-3"><Form.Item label="Trend" name={[field.name, "trend"]}><Input /></Form.Item></div>
                            <div className="col-md-3"><Form.Item label="Salary" name={[field.name, "salary"]}><Input /></Form.Item></div>
                          </div>
                          <Form.Item label="Description" name={[field.name, "description"]}><Input.TextArea rows={2} /></Form.Item>
                        </Card>
                      ))}
                      <Button type="dashed" block onClick={() => add()}>Add Career Outcome</Button>
                    </div>
                  )}
                </Form.List>

                <Divider orientation="left">Roadmap Stages</Divider>
                {(editingPage?.stages || []).map((stage, index) => (
                  <Card
                    key={stage.level}
                    size="small"
                    title={`${STAGE_LEVEL_LABELS[stage.level]} Stage`}
                    style={{ marginBottom: 12 }}
                    extra={<Tag color="blue">{stage.level}</Tag>}
                  >
                    <div className="row">
                      <div className="col-md-4"><Form.Item label="Title" name={["stages", index, "title"]}><Input /></Form.Item></div>
                      <div className="col-md-4"><Form.Item label="Subtitle" name={["stages", index, "subtitle"]}><Input /></Form.Item></div>
                      <div className="col-md-4"><Form.Item label="Roadmap Step" name={["stages", index, "roadmapStep"]}><Input /></Form.Item></div>
                    </div>
                    <Form.Item label="Description" name={["stages", index, "description"]}><Input.TextArea rows={2} /></Form.Item>
                    <div className="row">
                      <div className="col-md-4"><Form.Item label="Learning Objective" name={["stages", index, "learningObjective"]}><Input /></Form.Item></div>
                      <div className="col-md-4"><Form.Item label="Expected Outcome" name={["stages", index, "expectedOutcome"]}><Input /></Form.Item></div>
                      <div className="col-md-4"><Form.Item label="Estimated Duration" name={["stages", index, "estimatedDuration"]}><Input /></Form.Item></div>
                    </div>
                    <Form.Item label="Learning Intent" name={["stages", index, "learningIntent"]}><Input.TextArea rows={2} /></Form.Item>
                  </Card>
                ))}

                <Divider orientation="left">FAQ</Divider>
                <Form.List name="faq">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.map((field) => (
                        <Card key={field.key} size="small" style={{ marginBottom: 12 }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                          <Form.Item label="Question" name={[field.name, "question"]}><Input /></Form.Item>
                          <Form.Item label="Answer" name={[field.name, "answer"]}><Input.TextArea rows={2} /></Form.Item>
                        </Card>
                      ))}
                      <Button type="dashed" block onClick={() => add()}>Add FAQ</Button>
                    </div>
                  )}
                </Form.List>

                <Divider orientation="left">Ecosystem</Divider>
                <Form.Item label="Related Ecosystems">
                  <StringList name="relatedEcosystems" addLabel="Add Related Slug" placeholder="skill-slug" />
                </Form.Item>
                <Form.Item label="Semantic Context" name="semanticContext"><Input.TextArea rows={3} /></Form.Item>
                <Form.Item label="Career Progression Note" name="careerProgressionNote"><Input.TextArea rows={3} /></Form.Item>
                <Form.List name="skillPairings">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.map((field) => (
                        <Card key={field.key} size="small" style={{ marginBottom: 12 }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                          <div className="row">
                            <div className="col-md-4"><Form.Item label="Slug" name={[field.name, "slug"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Name" name={[field.name, "name"]}><Input /></Form.Item></div>
                          </div>
                          <Form.Item label="Relationship Context" name={[field.name, "relationshipContext"]}><Input.TextArea rows={2} /></Form.Item>
                        </Card>
                      ))}
                      <Button type="dashed" block onClick={() => add()}>Add Skill Pairing</Button>
                    </div>
                  )}
                </Form.List>
              </Form>
            )}
          </Drawer>

          <Modal
            open={!!previewPage}
            onCancel={() => setPreviewPage(null)}
            footer={<Button onClick={() => setPreviewPage(null)}>Close</Button>}
            width={860}
            title={previewPage ? `Preview: ${previewPage.name}` : "Preview"}
          >
            {previewPage && (
              <div>
                <Space style={{ marginBottom: 12 }}>
                  <Tag color={PAGE_STATUS_COLORS[previewPage.status]}>{previewPage.status}</Tag>
                  <Tag>{previewPage.slug}</Tag>
                </Space>
                <Typography.Title level={3}>{previewPage.name}</Typography.Title>
                <Typography.Paragraph>{previewPage.subtitle}</Typography.Paragraph>
                <Typography.Paragraph>{previewPage.overview?.description}</Typography.Paragraph>
                <Divider />
                <Typography.Title level={4}>Roadmap</Typography.Title>
                {(previewPage.stages || []).map((stage) => (
                  <Card key={stage.level} size="small" style={{ marginBottom: 8 }}>
                    <Tag color="blue">{stage.level}</Tag>
                    <strong style={{ marginLeft: 8 }}>{stage.title}</strong>
                    <div style={{ marginTop: 6 }}>{stage.description}</div>
                  </Card>
                ))}
                <Divider />
                <Typography.Title level={4}>FAQ</Typography.Title>
                {(previewPage.faq || []).map((item, index) => (
                  <div key={index} style={{ marginBottom: 10 }}>
                    <strong>{item.question}</strong>
                    <div>{item.answer}</div>
                  </div>
                ))}
              </div>
            )}
          </Modal>

        </div>
      </div>
    </Page>
  );
};

export default SkillList;
